import { checkoutMachine } from "@/components/Checkout/CheckoutMachine";
import { ActorLogicFrom, ActorRefFrom } from "xstate";
import {
  RetailerCheckoutOrderStatus,
  deleteOutOfStockItemsFromCheckoutOrder,
  updateUserConfirmationToCheckoutItems,
} from "../../api/rest/checkoutApi";
import { UserPurchaseHistory } from "../../types/misc";
import { formatMoney, getEntityImage } from "../../utils/StringUtils";
import {
  CheckoutSection,
  CheckoutSectionDisplayStage,
  ShippingAddress,
  ShoppingCartItem,
  UserInteractionType,
  UserRetailerCheckoutOrder,
  UserRetailerCheckoutOrderStatusV2,
  UserRetailerShoppingCart,
} from "./types";
import {
  ACTION_REQUIRED_ERROR,
  NON_ACTION_REQUIRED_ERROR,
  SessionOpenState,
} from "./CheckoutMachine/types/FastCheckoutMachine";
import _cloneDeep from "lodash/cloneDeep";
import { getRetailerFromId } from "@brandclub/common-ui";
export enum retailerLimits {
  "maximumQuantityPerProduct" = 10,
  "maxProductCount" = 20,
}

export const isOrderInFinalStage = (
  order?: UserRetailerCheckoutOrder
): boolean => {
  return (
    order?.status === UserRetailerCheckoutOrderStatusV2.checkout_error ||
    order?.status === UserRetailerCheckoutOrderStatusV2.checkout_success ||
    order?.status === UserRetailerCheckoutOrderStatusV2.checkout_cancelled
  );
};

const getTotal = (products: ShoppingCartItem[]) => {
  return products.reduce((acc, item) => {
    return acc + item.retailPrice * item.quantity;
  }, 0);
};

export const calculateTotalOfAllCarts = (
  carts: Partial<UserRetailerShoppingCart>[]
) => {
  const total = carts.reduce((acc, cart) => {
    return acc + getTotal(cart.items ?? []);
  }, 0);
  return formatMoney(total);
};

export const calculateTotal = (products: ShoppingCartItem[]) => {
  const total = getTotal(products);

  return formatMoney(total);
};

export const isItemCheckoutSuccess = (
  item: ShoppingCartItem | UserPurchaseHistory
) => {
  return (
    !item.status ||
    item.status === "success" ||
    item.status === "partial_success"
  );
};

export const isPurchaseCheckoutSuccess = (
  p: UserRetailerCheckoutOrder | UserPurchaseHistory | undefined
) => {
  return p?.status === "checkout_success";
};

type ItemWithQuantity = {
  quantity?: number;
};
type ItemWithItems = {
  items?: ItemWithQuantity[];
};
export const sumItemQuantities = (
  items: (ItemWithQuantity | undefined)[] | undefined
) => {
  if (!items) {
    return 0;
  }
  return items.reduce((total, item) => total + (item?.quantity ?? 0), 0);
};

export const sumAllItemQuantities = (item: (ItemWithItems | undefined)[]) => {
  return item.reduce((total, i) => total + sumItemQuantities(i?.items), 0);
};

export const flattenCartItems = (carts: Partial<UserRetailerShoppingCart>[]) =>
  carts
    .flatMap((c) => c.items)
    .filter((i): i is ShoppingCartItem => Boolean(i));

export const createRetailerSkuMap = (
  carts: Partial<UserRetailerShoppingCart>[]
) => new Map(flattenCartItems(carts).map((item) => [item.retailerSku, item]));

export const buildAddressString = (
  shippingAddress?: Partial<ShippingAddress>
): string => {
  const addressParts = [
    shippingAddress?.address1,
    shippingAddress?.address2,
    shippingAddress?.city,
    shippingAddress?.state,
  ];
  const address = addressParts
    .map((str, index) => {
      if (index > 1) {
        return str?.trim();
      }
      return str;
    })
    .filter((str) => !!str)
    .join(", ");
  if (addressParts.join("").trim() === "") {
    return "";
  }
  const zipCode = shippingAddress?.zipCode?.trim();
  if (zipCode) {
    return address + " " + zipCode;
  }
  return address;
};

export const getStepFromCartProducts = (
  cartProducts: ShoppingCartItem[] | undefined
) => {
  if (!cartProducts) {
    return [];
  }

  return cartProducts.map((product, index) => {
    return {
      title:
        cartProducts.length > 1
          ? `Checking item ${index + 1} of ${cartProducts.length}`
          : "Checking item",
      subtitle: `Quantity of ${product.quantity}`,
      imageUrl: getEntityImage(product.stacklineSku, "product"),
      visible: true,
      success: product.status === "pending" || isItemCheckoutSuccess(product),
      id: product.stacklineSku,
    };
  });
};

export const getOrderTotals = (
  order: UserRetailerCheckoutOrder | undefined
) => {
  if (!order) {
    return {
      subtotal: 0,
      shippingFees: 0,
      shippingDiscount: 0,
      shipping: 0,
      tax: 0,
      total: 0,
      giftCardAmount: 0,
      serviceFee: 0,
    };
  }
  const orderSummary =
    order.orderSummary ?? order.userCheckoutInfo?.estimatedOrderSummary;
  const subtotal = orderSummary?.subtotal ?? 0;
  const shippingFees = orderSummary?.shippingFees ?? 0;
  const shippingDiscount = Math.abs(orderSummary?.shippingDiscount ?? 0);
  const shipping = shippingFees - shippingDiscount;
  const tax = orderSummary?.tax ?? 0;
  const total = orderSummary?.total ?? 0;
  const giftCardAmount = orderSummary?.giftCard ?? 0;
  const serviceFee = orderSummary?.serviceFee ?? 0;
  return {
    subtotal,
    shippingFees,
    shippingDiscount,
    shipping,
    tax,
    total,
    giftCardAmount,
    serviceFee,
  };
};

export const getSortedAddresses = (
  addresses: UserRetailerCheckoutOrder["availableAddresses"] = []
) => {
  const availableAddresses = addresses
    .slice()
    .sort((a, b) => {
      return a?.extendedAttributes?.isSelected
        ? -1
        : b?.extendedAttributes?.isSelected
        ? 1
        : 0;
    })
    .filter(
      (address) =>
        address &&
        address.firstName &&
        address.address1 &&
        address.city &&
        address.state &&
        address.zipCode
    );

  const filteredAddresses = availableAddresses.filter((address) => {
    const notAmazonFresh = !address?.firstName
      .toLocaleLowerCase()
      ?.includes("amazon fresh");
    const notWholeFoods = !address?.firstName
      .toLocaleLowerCase()
      ?.includes("whole foods");
    const notUPS = !address?.firstName.toLocaleLowerCase()?.includes("ups");
    return notAmazonFresh && notWholeFoods && notUPS;
  });

  const sortedShippingAddresses = filteredAddresses.sort((a, b) => {
    return a?.extendedAttributes?.isSelected
      ? -1
      : b?.extendedAttributes?.isSelected
      ? 1
      : 0;
  });

  return sortedShippingAddresses;
};

export const checkBagFull = (
  items: ShoppingCartItem[],
  allCarts: UserRetailerShoppingCart[]
) => {
  const itemCountByRetailerId = new Map<number, number>();
  const itemCountByProduct = new Map<string, number>();

  for (const item of items) {
    const retailerCount = itemCountByRetailerId.get(item.retailerId) ?? 0;
    itemCountByRetailerId.set(item.retailerId, retailerCount + item.quantity);

    const productCount = itemCountByProduct.get(item.stacklineSku) ?? 0;
    itemCountByProduct.set(item.stacklineSku, productCount + item.quantity);
  }

  for (const cart of allCarts) {
    for (const item of cart.items) {
      const productCount = itemCountByProduct.get(item.stacklineSku) ?? 0;
      const retailerCount = itemCountByRetailerId.get(cart.retailerId) ?? 0;

      itemCountByProduct.set(item.stacklineSku, productCount + item.quantity);
      itemCountByRetailerId.set(cart.retailerId, retailerCount + item.quantity);

      if (
        productCount + item.quantity >
        retailerLimits.maximumQuantityPerProduct
      ) {
        return true;
      }
    }
  }

  return false;
};

export const removeErrorProduct = async (
  stacklineSku: string,
  retailerCheckoutOrderStatus: RetailerCheckoutOrderStatus
) => {
  const removeItem =
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.items?.find(
      (item) => item.stacklineSku === stacklineSku
    );
  if (!removeItem) {
    return;
  }

  const request = {
    retailerId:
      retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.retailerId,
    orderId: retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.orderId,
    brandId: retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.brandId,
    items: [
      {
        quantity: removeItem?.quantity,
        retailPrice: removeItem?.retailPrice,
        retailerSku: removeItem?.retailerSku,
        retailerId: removeItem?.retailerId,
      },
    ],
  };
  try {
    return await deleteOutOfStockItemsFromCheckoutOrder(request);
  } catch (e) {
    console.error("error", JSON.stringify(e));
  }
};

export const confirmPartiallyOutOfStock = async (
  stacklineSku: string,
  retailerCheckoutOrderStatus: RetailerCheckoutOrderStatus,
  send: ActorRefFrom<ActorLogicFrom<typeof checkoutMachine>>["send"]
) => {
  const removeItem =
    retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.items?.find(
      (item) => item.stacklineSku === stacklineSku
    );
  if (!removeItem) {
    return;
  }
  const request = {
    retailerId:
      retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.retailerId,
    orderId: retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.orderId,
    brandId: retailerCheckoutOrderStatus?.userRetailerCheckoutOrder?.brandId,
    items: [
      {
        quantity: removeItem?.quantity,
        retailPrice: removeItem?.retailPrice,
        retailerSku: removeItem?.retailerSku,
        retailerId: removeItem?.retailerId,
      },
    ],
  };

  try {
    const res = await updateUserConfirmationToCheckoutItems(request);
    send({
      type: "ON_CHECKOUT_STATUS_CHANGE",
      data: {
        retailerCheckoutOrderStatus: res,
      },
    });
  } catch (e) {
    console.error("error", JSON.stringify(e));
  }
};

export const getActiveStageFromCheckoutSection = (
  displayStage?: CheckoutSectionDisplayStage
) => {
  switch (displayStage) {
    case CheckoutSectionDisplayStage.CONNECTING_RETAILER:
      return 1;
    case CheckoutSectionDisplayStage.VERIFYING_PAYMENT_METHOD:
      return 2;
    case CheckoutSectionDisplayStage.VERIFYING_INVENTORY_AND_DELIVERY:
      return 3;
    case CheckoutSectionDisplayStage.PLACE_ORDER_SUCCESS:
      return 4;
    default:
      return 0;
  }
};

export const getErrorStageNumberFromCheckoutSection = (
  section?: CheckoutSection | CheckoutSectionDisplayStage
) => {
  if (!section) {
    return 0;
  }
  switch (section) {
    case CheckoutSection.CONNECTING_RETAILER:
    case CheckoutSectionDisplayStage.CONNECTING_RETAILER:
      return 1;
    case CheckoutSection.VALIDATING_PAYMENT_METHOD:
    case CheckoutSectionDisplayStage.VERIFYING_PAYMENT_METHOD:
      return 2;
    case CheckoutSection.VALIDATING_SHIPPING_ADDRESS:
    case CheckoutSection.VALIDATING_ORDER_ITEMS:
    case CheckoutSection.VALIDATING_ORDER_SUMMARY:
    case CheckoutSection.PLACING_ORDER:
    case CheckoutSectionDisplayStage.VERIFYING_INVENTORY_AND_DELIVERY:
      return 3;
    case CheckoutSection.PLACE_ORDER_SUCCESS:
    case CheckoutSectionDisplayStage.PLACE_ORDER_SUCCESS:
      return 4;
    default:
      return 0;
  }
};

export const getErrorTypeFromCheckoutOrder = (
  order?: UserRetailerCheckoutOrder
): ACTION_REQUIRED_ERROR | NON_ACTION_REQUIRED_ERROR | undefined => {
  if (!order?.userInteractionRequirement) {
    return undefined;
  }
  const userInteractionType = order.userInteractionRequirement.type;
  switch (userInteractionType) {
    case UserInteractionType.RETAILER_CREDENTIALS:
      return ACTION_REQUIRED_ERROR.LOGIN_ERROR;
    case UserInteractionType.OTP_VERIFICATION:
      return ACTION_REQUIRED_ERROR.OTP_ERROR;
    case UserInteractionType.ADDRESS_SELECTION:
      return ACTION_REQUIRED_ERROR.SHIPPING_ERROR;
    case UserInteractionType.INVENTORY_CONFIRMATION:
      return ACTION_REQUIRED_ERROR.DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR;
    case UserInteractionType.SHIPPING_OPTION_SELECTION:
      return ACTION_REQUIRED_ERROR.DELIVERY_PARTIALLY_OUT_OF_STOCK_ERROR;
    default:
      return undefined;
  }
};

export const getSectionStateFromCheckoutOrder = (
  currentSectionState: {
    account: SessionOpenState;
    payment: SessionOpenState;
    shipping: SessionOpenState;
    delivery: SessionOpenState;
  },
  order?: UserRetailerCheckoutOrder
) => {
  // Create a deep copy of the current section state to modify
  const sectionState = _cloneDeep(currentSectionState);

  const isFinalStage = isOrderInFinalStage(order);

  const retryCheckout =
    order?.status ===
    UserRetailerCheckoutOrderStatusV2.user_interaction_timeout;

  // Track which sections were previously in error state (for transitioning)
  const previousErrorSections = {
    account: !!currentSectionState.account.errorMessage,
    shipping: !!currentSectionState.shipping.errorMessage,
    delivery: !!currentSectionState.delivery.errorMessage,
    payment: !!currentSectionState.payment.errorMessage,
  };

  // Reset all error messages and make chevrons visible by default
  Object.keys(sectionState).forEach((key) => {
    sectionState[key as keyof typeof sectionState].chevronShow = true;
    sectionState[key as keyof typeof sectionState].errorMessage = undefined;
  });

  // If we have an error condition, update the relevant section
  if (order?.userInteractionRequirement) {
    if (
      order.userInteractionRequirement.type ===
        UserInteractionType.RETAILER_CREDENTIALS ||
      order.userInteractionRequirement.type ===
        UserInteractionType.OTP_VERIFICATION
    ) {
      sectionState.account.chevronShow = false;
      sectionState.account.open = true;
      sectionState.account.errorMessage =
        order.userInteractionRequirement.displayMessage;
    } else if (
      order.userInteractionRequirement.type ===
      UserInteractionType.ADDRESS_SELECTION
    ) {
      sectionState.shipping.chevronShow = false;
      sectionState.shipping.open = true;
      sectionState.shipping.errorMessage =
        order.userInteractionRequirement.displayMessage;
    } else if (
      order.userInteractionRequirement.type ===
        UserInteractionType.INVENTORY_CONFIRMATION ||
      order.userInteractionRequirement.type ===
        UserInteractionType.SHIPPING_OPTION_SELECTION
    ) {
      sectionState.delivery.chevronShow = false;
      sectionState.delivery.open = true;
      sectionState.delivery.errorMessage =
        order.userInteractionRequirement.displayMessage;
    } else if (
      order.userInteractionRequirement.type ===
      UserInteractionType.NO_INVENTORY_AVAILABLE
    ) {
      sectionState.delivery.chevronShow = false;
      sectionState.delivery.open = true;
      sectionState.delivery.errorMessage =
        order.userInteractionRequirement.displayMessage;
    }
  }

  // Close sections that were transitioning from error to non-error
  // But only if they weren't manually opened by the user
  if (
    previousErrorSections.account &&
    !sectionState.account.errorMessage &&
    !currentSectionState.account.chevronShow
  ) {
    sectionState.account.open = false;
  }
  if (
    previousErrorSections.shipping &&
    !sectionState.shipping.errorMessage &&
    !currentSectionState.shipping.chevronShow
  ) {
    sectionState.shipping.open = false;
  }
  if (
    previousErrorSections.delivery &&
    !sectionState.delivery.errorMessage &&
    !currentSectionState.delivery.chevronShow
  ) {
    sectionState.delivery.open = false;
  }
  if (
    previousErrorSections.payment &&
    !sectionState.payment.errorMessage &&
    !currentSectionState.payment.chevronShow
  ) {
    sectionState.payment.open = false;
  }

  if (!order?.userInteractionRequirement?.type) {
    sectionState.shipping.errorMessage = undefined;
    sectionState.shipping.open = false;
    sectionState.shipping.chevronShow = false;
    sectionState.delivery.errorMessage = undefined;
    sectionState.delivery.open = false;
    sectionState.delivery.chevronShow = false;
  }

  if (isFinalStage || retryCheckout) {
    sectionState.account.chevronShow = false;
    sectionState.shipping.chevronShow = false;
    sectionState.delivery.chevronShow = false;
    sectionState.payment.chevronShow = false;
    sectionState.account.open = false;
    sectionState.shipping.open = false;
    sectionState.delivery.open = false;
    sectionState.payment.open = false;
    sectionState.account.errorMessage = undefined;
    sectionState.shipping.errorMessage = undefined;
    sectionState.delivery.errorMessage = undefined;
    sectionState.payment.errorMessage = undefined;
  }

  return sectionState;
};

export const getCheckoutHeaderRenderConfig = ({
  displayStage,
  order,
  displayErrorMessage,
  forceError
}: {
  displayStage?: CheckoutSectionDisplayStage,
  order?: UserRetailerCheckoutOrder,
  displayErrorMessage?: string,
  forceError?: boolean
}): {
  headerLabel: string;
  headerText: string;
  error: boolean;
  actionButton?: "retry_checkout" | "view_on_retailer_site";
} => {
  const currentRetailerId = order?.retailerId;

  const config: {
    headerLabel: string;
    headerText: string;
    error: boolean;
    actionButton?: "retry_checkout" | "view_on_retailer_site";
  } = {
    headerLabel: "Processing your order",
    headerText: "",
    error: forceError ?? false,
    actionButton: undefined,
  };

  if (!order) {
    return config;
  }

  if (displayStage) {
    switch (displayStage) {
      case CheckoutSectionDisplayStage.CONNECTING_RETAILER:
        config.headerText = `Connecting your ${
          currentRetailerId ? getRetailerFromId(currentRetailerId) : "retailer"
        } account`;
        break;
      case CheckoutSectionDisplayStage.VERIFYING_PAYMENT_METHOD:
        config.headerText = "Verifying your payment method";
        break;
      case CheckoutSectionDisplayStage.VERIFYING_INVENTORY_AND_DELIVERY:
        config.headerText = "Confirming inventory and delivery";
        break;
      case CheckoutSectionDisplayStage.PLACE_ORDER_SUCCESS:
        config.headerText = "Order Placed";
        break;
      case CheckoutSectionDisplayStage.PLACE_ORDER_FAILED:
        config.headerText = "Order Failed";
        break;
      default:
        break;
    }
  }
  if (order?.userInteractionRequirement?.type) {
    config.error = true;
    if (
      order.userInteractionRequirement.type ===
      UserInteractionType.RETAILER_CREDENTIALS
    ) {
      config.headerLabel = `Continue with ${
        currentRetailerId ? getRetailerFromId(currentRetailerId) : "retailer"
      }`;
    }
  }

  // TODO:payment error
  // TODO:all out of stock

  // Generic timeout error
  if (order?.status === "user_interaction_timeout") {
    config.error = forceError ?? false;
    config.headerText =
      "Your order session expired because some steps weren't completed. Please try again and watch for any updates from us about your order.";
    config.actionButton = "retry_checkout";
  } else if (order?.status === "checkout_error") {
    config.error = forceError ?? false;
    //
    config.headerText = displayErrorMessage ?? 'We couldn’t process your order';
    config.actionButton = "view_on_retailer_site";
  }

  return config;
};
